import { z } from 'zod'

// Os valores são os mesmos nomes dos enums do Prisma, para não haver tradução entre
// banco, API e app. O mapa para os códigos do Dataverse vive só no migrador.

export const TipoCampo = {
  MULTISSELECAO: 'MULTISSELECAO',
  SELECAO_UNICA: 'SELECAO_UNICA',
  TEXTO: 'TEXTO',
  IMAGEM: 'IMAGEM',
  NUMERO: 'NUMERO',
  DATA: 'DATA',
  ASSINATURA: 'ASSINATURA',
  SIM_NAO: 'SIM_NAO',
} as const
export type TipoCampo = (typeof TipoCampo)[keyof typeof TipoCampo]
export const tipoCampoSchema = z.nativeEnum(TipoCampo)

export const StatusInspecao = {
  RASCUNHO: 'RASCUNHO',
  EM_ANDAMENTO: 'EM_ANDAMENTO',
  FINALIZADO: 'FINALIZADO',
  ASSINADO: 'ASSINADO',
} as const
export type StatusInspecao = (typeof StatusInspecao)[keyof typeof StatusInspecao]
export const statusInspecaoSchema = z.nativeEnum(StatusInspecao)

/// A criticidade que o inspetor marca no campo "Criticidade" do checklist.
export const Criticidade = {
  LEVE: 'LEVE',
  MODERADA: 'MODERADA',
  ALTA: 'ALTA',
} as const
export type Criticidade = (typeof Criticidade)[keyof typeof Criticidade]
export const criticidadeSchema = z.nativeEnum(Criticidade)

/// A revisão do SESMT. **Escala diferente da do inspetor** — Alta, Média, Baixa —
/// e é assim no Power Apps: a do inspetor descreve o achado na hora, esta
/// classifica o risco depois da análise. Não misturar as duas.
export const CriticidadeApia = {
  BAIXA: 'BAIXA',
  MEDIA: 'MEDIA',
  ALTA: 'ALTA',
} as const
export type CriticidadeApia = (typeof CriticidadeApia)[keyof typeof CriticidadeApia]
export const criticidadeApiaSchema = z.nativeEnum(CriticidadeApia)

export const TipoChecklist = { INI: 'INI', DINAMICO: 'DINAMICO' } as const
export type TipoChecklist = (typeof TipoChecklist)[keyof typeof TipoChecklist]

export const Perfil = {
  SUPERADMIN: 'SUPERADMIN',
  ADMIN: 'ADMIN',
  GERENTE_CONTRATO: 'GERENTE_CONTRATO',
  ENGENHEIRO_SEGURANCA: 'ENGENHEIRO_SEGURANCA',
  TECNICO_SEGURANCA: 'TECNICO_SEGURANCA',
  ENCARREGADO: 'ENCARREGADO',
  COLABORADOR: 'COLABORADOR',
  /// Só uma marca: põe a pessoa na lista de responsáveis das não conformidades
  /// das obras dela. Não dá acesso nem permissão, e o RM não o dá nem o tira.
  RESPONSAVEL_NC: 'RESPONSAVEL_NC',
} as const
export type Perfil = (typeof Perfil)[keyof typeof Perfil]
export const perfilSchema = z.nativeEnum(Perfil)

/// Rótulos para a interface. O banco guarda o enum; a tela mostra isto.
export const rotuloTipoCampo: Record<TipoCampo, string> = {
  MULTISSELECAO: 'Multisseleção',
  SELECAO_UNICA: 'Seleção única',
  TEXTO: 'Texto',
  IMAGEM: 'Imagem',
  NUMERO: 'Número',
  DATA: 'Data',
  ASSINATURA: 'Assinatura',
  SIM_NAO: 'Sim/Não',
}

export const rotuloStatus: Record<StatusInspecao, string> = {
  RASCUNHO: 'Rascunho',
  EM_ANDAMENTO: 'Em andamento',
  FINALIZADO: 'Finalizado',
  ASSINADO: 'Assinado',
}

export const rotuloCriticidade: Record<Criticidade, string> = {
  LEVE: 'Leve',
  MODERADA: 'Moderada',
  ALTA: 'Alta',
}

export const rotuloCriticidadeApia: Record<CriticidadeApia, string> = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  ALTA: 'Alta',
}

export const rotuloPerfil: Record<Perfil, string> = {
  SUPERADMIN: 'Administrador supremo',
  ADMIN: 'Administrador',
  GERENTE_CONTRATO: 'Gerente de contrato',
  ENGENHEIRO_SEGURANCA: 'Engenheiro de segurança',
  TECNICO_SEGURANCA: 'Técnico de segurança',
  ENCARREGADO: 'Encarregado',
  COLABORADOR: 'Colaborador',
  RESPONSAVEL_NC: 'Responsável por NC',
}

// -------------------------------------------------------------- entidades

export const campoChecklistSchema = z.object({
  id: z.string().uuid(),
  checklistId: z.string().uuid(),
  nome: z.string(),
  tipoCampo: tipoCampoSchema,
  opcoes: z.array(z.string()).default([]),
  secao: z.string().nullable().optional(),
  itemCode: z.string().nullable().optional(),
  ordem: z.number().int().default(0),
  obrigatorio: z.boolean().default(false),
})
export type CampoChecklist = z.infer<typeof campoChecklistSchema>

export const checklistSchema = z.object({
  id: z.string().uuid(),
  codigo: z.string().nullable().optional(),
  nome: z.string(),
  categoria: z.string().nullable().optional(),
  revisao: z.string().nullable().optional(),
  descricao: z.string().nullable().optional(),
  tipo: z.nativeEnum(TipoChecklist),
  ativo: z.boolean().default(true),
  campos: z.array(campoChecklistSchema).default([]),
})
export type Checklist = z.infer<typeof checklistSchema>

export const obraSchema = z.object({
  id: z.string().uuid(),
  codigo: z.string(),
  nome: z.string(),
  localizacao: z.string().nullable().optional(),
})
export type Obra = z.infer<typeof obraSchema>

/// Valor de uma resposta. Só um dos campos é preenchido, conforme o tipo.
export const valorRespostaSchema = z.object({
  texto: z.string().nullable().optional(),
  textoLongo: z.string().nullable().optional(),
  numero: z.number().nullable().optional(),
  /// aaaa-mm-dd — data pura, sem fuso, para não virar o dia ao atravessar UTC.
  data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  booleano: z.boolean().nullable().optional(),
  opcoes: z.array(z.string()).default([]),
  arquivoId: z.string().uuid().nullable().optional(),
  /// Tratativa da não conformidade (resposta "NC"): quem corrige, o que será feito
  /// e até quando. Fica na própria resposta, porque cada NC tem a sua — é por ela
  /// que depois se mede o lead time de cada item. Nas outras respostas, nulo.
  /// Limites folgados: a tela corta antes (120 e 2.000), e um lote recusado aqui
  /// travaria o envio inteiro do aparelho.
  ncResponsavel: z.string().max(300).nullable().optional(),
  /// A pessoa escolhida na lista de responsáveis da obra. Nulo quando o nome foi
  /// digitado ("não está na lista"). Sem `.uuid()`: um id torto recusaria o lote
  /// inteiro; o servidor confere e, se não achar a pessoa, fica só o nome.
  ncResponsavelId: z.string().max(64).nullable().optional(),
  ncAcao: z.string().max(5000).nullable().optional(),
  /// aaaa-mm-dd. Sem a regex de `data` de propósito: um formato torto aqui
  /// recusaria o lote inteiro com 400 e o aparelho retentaria para sempre. Quem
  /// confere é `validarResposta`, que recusa só a inspeção, e só ao finalizar.
  ncPrazo: z.string().max(30).nullable().optional(),
})
export type ValorResposta = z.infer<typeof valorRespostaSchema>

export const respostaSchema = valorRespostaSchema.extend({
  id: z.string().uuid(),
  campoChecklistId: z.string().uuid(),
  campoNome: z.string(),
  campoTipo: tipoCampoSchema,
  ordem: z.number().int().default(0),
})
export type Resposta = z.infer<typeof respostaSchema>

export const fotoSchema = z.object({
  id: z.string().uuid(),
  arquivoId: z.string().uuid(),
  descricao: z.string().nullable().optional(),
  ordem: z.number().int().default(0),
})
export type Foto = z.infer<typeof fotoSchema>

export const inspecaoSchema = z.object({
  id: z.string().uuid(),
  codigo: z.string(),
  checklistId: z.string().uuid(),
  obraId: z.string().uuid(),
  status: statusInspecaoSchema,
  titulo: z.string().nullable().optional(),
  dataCriacao: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  criticidadeApia: criticidadeApiaSchema.nullable().optional(),
  assinaturaUsuario: z.string().max(100).nullable().optional(),
  assinaturaRevisor: z.string().max(100).nullable().optional(),
  registradoEm: z.string().datetime(),
  versao: z.number().int().default(1),
  respostas: z.array(respostaSchema).default([]),
  fotos: z.array(fotoSchema).default([]),
})
export type Inspecao = z.infer<typeof inspecaoSchema>

export const usuarioSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  nome: z.string(),
  perfis: z.array(perfilSchema).default([]),
  obras: z.array(obraSchema).default([]),
})
export type Usuario = z.infer<typeof usuarioSchema>
